// First-run choice screen for the PWA. Two transports:
//   - Online: PeerJS broker for signalling, then peer-to-peer audio.
//   - Offline: manual QR pairing, then peer-to-peer audio.
// Audio always flows directly between browsers over WebRTC DataChannel.

import { useState } from 'react'
import { InstallPrompt } from './InstallPrompt'

type Mode =
  | 'browser-host'
  | 'browser-join'
  | 'offline-host'
  | 'offline-join'

export function Splash({ onPick }: { onPick: (m: Mode) => void }) {
  const [code, setCode] = useState('')
  return (
    <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-6 py-10">
      <header>
        <h1 className="text-3xl font-semibold tracking-tight">Multispeaker</h1>
        <p className="mt-2 text-sm text-zinc-400">
          Turn a roomful of phones, laptops, and tablets into one synchronized speaker.
          Browser-only — works offline once installed.
        </p>
      </header>

      <section className="mt-8 space-y-3">
        <button
          type="button"
          onClick={() => onPick('browser-host')}
          className="w-full rounded-xl bg-blue-500 px-4 py-4 text-left text-white shadow-sm transition active:scale-[0.98]"
        >
          <div className="text-base font-semibold">Start a session</div>
          <div className="text-xs text-blue-100/85">
            Use this device to play music for everyone else.
          </div>
        </button>

        <form
          onSubmit={(e) => {
            e.preventDefault()
            const trimmed = code.trim()
            if (!trimmed) return
            const u = new URL(window.location.href)
            u.searchParams.set('join', trimmed)
            window.history.replaceState(null, '', u.toString())
            onPick('browser-join')
          }}
          className="rounded-xl bg-zinc-900/40 p-4 ring-1 ring-zinc-800"
        >
          <label className="block text-xs uppercase tracking-wide text-zinc-400">
            Join a session
          </label>
          <div className="mt-2 flex gap-2">
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="Code, e.g. AB34CD"
              autoComplete="off"
              spellCheck={false}
              className="flex-1 rounded-md bg-zinc-950 px-3 py-2 font-mono text-sm uppercase tracking-widest text-zinc-100 placeholder:text-zinc-600 ring-1 ring-zinc-700 focus:outline-none focus:ring-blue-400"
            />
            <button
              type="submit"
              disabled={code.trim().length === 0}
              className="rounded-md bg-blue-500 px-3 text-sm font-medium text-white disabled:bg-zinc-700"
            >
              Join
            </button>
          </div>
        </form>
      </section>

      <InstallPrompt />

      <details className="mt-6 text-xs text-zinc-500">
        <summary className="cursor-pointer text-zinc-400">
          No internet? Pair offline with QR codes.
        </summary>
        <p className="mt-2">
          Works with any Wi-Fi (even a hotspot with no WAN) — devices swap one QR
          code each at setup, then talk peer-to-peer over the LAN.
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => onPick('offline-host')}
            className="rounded-md bg-zinc-800 px-3 py-1.5 text-sm font-medium text-zinc-100 ring-1 ring-zinc-700 transition active:scale-95"
          >
            Host offline session
          </button>
          <button
            type="button"
            onClick={() => onPick('offline-join')}
            className="rounded-md bg-zinc-800 px-3 py-1.5 text-sm font-medium text-zinc-100 ring-1 ring-zinc-700 transition active:scale-95"
          >
            Join via QR
          </button>
        </div>
      </details>
    </div>
  )
}
